import type { ReactNode } from 'react';
import { catalogHref } from '../lib/warehouse-preview-route';
import FinanceNavigation, { StatementNavigation } from './FinanceNavigation';

/**
 * Presentation only (#208 / #213). The future server must return ONLY reports
 * the verified caller may read, after evaluating every contributing source.
 * Titles, Entra groups and department names are never authorization inputs here.
 * Hiding a link is not API enforcement. This component performs no data reads.
 */
export interface CatalogReport {
  id: string;
  title: string;
}

export interface CatalogDepartment {
  id: 'finance' | 'growth';
  title: string;
  description: string;
  reports: readonly CatalogReport[];
}

export type CatalogState =
  | { state: 'loading' }
  | { state: 'unauthenticated' }
  | { state: 'denied' }
  | { state: 'error' }
  | { state: 'ready'; departments: readonly CatalogDepartment[]; pages: readonly ('growth')[] };

export interface DepartmentCatalogProps {
  catalog: CatalogState;
  selectedDepartment: string | null;
  onRetry: () => void;
  selectedReport?: string | null;
  renderReport?: (report: CatalogReport) => ReactNode;
  invalidRoute?: boolean;
  buildRef?: string;
  mode?: 'preview' | 'live';
  testControls?: ReactNode;
  selectedPage?: 'growth';
  growthPage?: ReactNode;
}

const reportCount = (count: number) => `${count} ${count === 1 ? 'report' : 'reports'}`;

export default function DepartmentCatalog({
  catalog, selectedDepartment, onRetry, selectedReport, renderReport, invalidRoute = false, buildRef = 'not supplied', mode = 'preview', testControls, selectedPage, growthPage,
}: DepartmentCatalogProps) {
  const href = (department: string | null, report: string | null = null) =>
    `${mode === 'live' ? '/' : ''}${catalogHref(department, report)}`;
  // No last-known-good catalog after a failure or revocation. Do not retain a
  // selected department object: re-resolve it against the CURRENT response.
  const departments = catalog.state === 'ready'
    ? catalog.departments.filter((department) => department.reports.length > 0) : [];
  const growthAllowed = mode === 'preview' || (catalog.state === 'ready' && catalog.pages?.includes('growth') === true);
  const selected = departments.find((department) => department.id === selectedDepartment);
  const report = selected?.reports.find((item) => item.id === selectedReport);
  const missingRoute = catalog.state === 'ready' && (invalidRoute || (selectedDepartment !== null && !selected) || (!!selectedReport && !report));
  const home = catalog.state === 'ready' && !selectedDepartment && !selectedPage && !invalidRoute && departments.length > 0;
  const finance = mode === 'live' && selected?.id === 'finance';
  const reportPage = mode === 'live' && report && !invalidRoute;

  return (
    <div className="warehouse-catalog">
      <a className="wc-skip" href="#warehouse-content" onClick={(event) => {
        event.preventDefault();
        const content = document.getElementById('warehouse-content');
        content?.focus(); content?.scrollIntoView();
      }}>Skip to catalog</a>
      <aside className="wc-sidebar">
        <div className="wc-brand"><span className="wc-wordmark">DISPEL</span><span>Data Warehouse</span></div>
        <nav aria-label="Warehouse navigation">
          <a href={href(null)} aria-current={home ? 'page' : undefined}>Home</a>
          {departments.length > 0 && <>
            <h2>Departments</h2>
            {departments.map((department) => (
              <a href={href(department.id)} key={department.id}
                aria-current={selected?.id === department.id ? 'page' : undefined}
              >
                <span>{department.title}</span>
                <span className="wc-count" aria-label={reportCount(department.reports.length)}>
                  {department.reports.length}
                </span>
              </a>
            ))}
          </>}
          {finance && <FinanceNavigation reports={selected.reports} selectedReport={selectedReport} />}
          {mode === 'live' && growthAllowed && <a href="/#/growth" aria-current={selectedPage === 'growth' ? 'page' : undefined}>Growth</a>}
          <h2>Planning</h2>
          {['Budgets', 'Forecast', 'Scenarios'].map((label) => <button key={label} type="button" disabled title="Not connected in this preview">{label}</button>)}
          <details className="wc-admin"><summary>Admin</summary>
            {['Access & RBAC', 'Audit Log'].map((label) => <button key={label} type="button" disabled title="Not connected in this preview">{label}</button>)}
          </details>
        </nav>
        <div className="wc-sidebar-note">Planning and admin are disabled design placeholders, not granted capabilities.</div>
        <div className="wc-service"><span className="wc-health-dot" aria-hidden="true" />
          {mode === 'preview' ? 'API not connected' : catalog.state === 'ready' ? 'Catalog received · data health not checked' : 'Catalog availability not established'}
          <br />Build: <code>{buildRef}</code>
          {mode === 'live' && <p><button className="wc-retry" type="button" onClick={onRetry}>Refresh access and data</button></p>}
        </div>
      </aside>

      <div className="wc-body">
        <header className="wc-header">
          <span className="wc-preview-badge">{mode === 'preview' ? 'Design preview · no live data' : 'Development · API-backed reports'}</span>
          <span>{mode === 'preview' ? 'Identity not connected' : 'Access evaluated by the server, not directory labels'}</span>
        </header>
        <main id="warehouse-content" className="wc-content" tabIndex={-1}>
          {testControls}
          {!selectedPage && <div className={`wc-intro${home ? ' wc-home-intro' : ''}${reportPage ? ' wc-report-intro' : ''}`}>
            {reportPage && selected && <p className="wc-breadcrumb"><a href={href(null)}>Departments</a> / <a href={href(selected.id)}>{selected.title}</a></p>}
            <h1>{reportPage ? report.title : selected ? selected.title : 'Data Warehouse'}</h1>
            {home ? <>
              <p className="wc-eyebrow">Phase 1: Proof of Concept</p><hr />
              <svg className="wc-illustration" viewBox="0 0 240 100" aria-hidden="true">
                <g fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M40 25H120V75H200M40 75H120V25H200" />
                  <rect x="12" y="10" width="56" height="30" rx="5" /><rect x="12" y="60" width="56" height="30" rx="5" />
                  <rect x="92" y="30" width="56" height="40" rx="5" /><rect x="172" y="10" width="56" height="30" rx="5" />
                  <rect x="172" y="60" width="56" height="30" rx="5" />
                </g>
              </svg>
            </> : selected && <p>{selected.description}</p>}
          </div>}

          {selectedPage === 'growth' && growthAllowed && growthPage}
          {selectedPage === 'growth' && mode === 'live' && !growthAllowed && <div className="wc-state" role="status">
            {catalog.state === 'loading' && <><h2>Loading access…</h2><p>Growth page availability has not been established.</p></>}
            {catalog.state === 'unauthenticated' && <><h2>Sign-in required</h2><p>No Growth page is shown without an authenticated session.</p></>}
            {catalog.state === 'denied' && <><h2>Growth access not granted</h2><p>Contact the access owner if you believe HubSpot access is missing.</p></>}
            {catalog.state === 'error' && <><h2>Access unavailable</h2><p>Growth availability could not be established. Try refreshing access and data.</p><button type="button" className="wc-retry" onClick={onRetry}>Try again</button></>}
            {catalog.state === 'ready' && <><h2>Growth access not granted</h2><p>The current catalog does not include the Growth page.</p></>}
          </div>}
          {!selectedPage && catalog.state === 'loading' && <div className="wc-state" role="status">
            <h2>Loading catalog…</h2><p>Report availability has not been established.</p>
          </div>}
          {!selectedPage && catalog.state === 'unauthenticated' && <div className="wc-state" role="status">
            <h2>Sign-in required</h2><p>No report catalog is shown without an authenticated session.</p>
          </div>}
          {!selectedPage && catalog.state === 'denied' && <div className="wc-state" role="status">
            <h2>Warehouse access not granted</h2><p>Contact the access owner if you believe access is missing.</p>
          </div>}
          {!selectedPage && catalog.state === 'error' && <div className="wc-state" role="alert">
            <h2>Catalog unavailable</h2>
            <p>We could not establish report availability. This does not mean you are signed out.</p>
            <button type="button" className="wc-retry" onClick={onRetry}>Try again</button>
          </div>}
          {!selectedPage && catalog.state === 'ready' && departments.length === 0 && !missingRoute && <div className="wc-state" role="status">
            <h2>No reports available</h2><p>The current catalog contains no reports you can open.</p>
          </div>}

          {!selectedPage && missingRoute && <div className="wc-state" role="status">
            <h2>{invalidRoute ? (mode === 'preview' ? 'Preview route not found' : 'Page not found') : selectedReport ? 'Report access not granted' : 'Department access not granted'}</h2>
            <p>{invalidRoute ? 'This is not a supported page URL.' : 'The requested item is not in your current catalog. It may be restricted or no longer available. No report data was loaded.'}</p>
            <p>{mode === 'preview' ? 'This is a simulated catalog decision, not a live server authorization check.' : 'Each data request must also pass server-side authorization.'}</p>
            <a className="wc-back" href={href(null)}>Return to available departments</a>
          </div>}
          {!selectedPage && home && (
            <section className="wc-departments" aria-label="Department catalog">
              {departments.map((department) => (
                <a href={href(department.id)} className="wc-department" key={department.id}>
                  <span className="wc-department-title">{department.title}</span>
                  <span className="wc-description">{department.description}</span>
                  <span className="wc-department-footer">
                    <span>{reportCount(department.reports.length)}</span><span aria-hidden="true">↗</span>
                  </span>
                </a>
              ))}
            </section>
          )}
          {!selectedPage && selected && <section className="wc-reports" aria-label={`${selected.title} reports`}>
            <a className="wc-back" href={href(null)}>← All departments</a>
            <p className="wc-section-note">Report availability follows the supplied catalog. Layout and design tokens remain provisional.</p>
            {finance ? (!reportPage && <FinanceNavigation reports={selected.reports} selectedReport={selectedReport} variant="cards" />) : <ul>{selected.reports.map((report) => (
              <li key={report.id}>
                <a href={href(selected.id, report.id)} aria-current={selectedReport === report.id ? 'page' : undefined}>
                  <span>{report.title}</span><span aria-hidden="true">→</span>
                </a>
              </li>
            ))}</ul>}
            {finance && reportPage && ['income-statement', 'balance-sheet'].includes(report.id)
              && <StatementNavigation reports={selected.reports} selectedReport={report.id} />}
            {!invalidRoute && report && renderReport?.(report)}
          </section>}
          {!selectedPage && <p className="wc-disclaimer">{mode === 'preview'
            ? 'Illustrative catalog only. Counts come from the selected preview fixture, not deployed reports or approved grants.'
            : 'Counts describe authorized API reports, not completed screens. Unimplemented screens are marked explicitly. Source and collection time appear with each report; a database load is not implied.'}</p>}
        </main>
      </div>
    </div>
  );
}
